import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import notificationService from "../../services/notificationService";

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  markAllAsReadLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
};

// Async Thunks
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationService.getNotifications(params);
      const payload = response.data?.data ?? response.data;
      return {
        notifications: payload?.notifications ?? [],
        unreadCount: payload?.unreadCount ?? 0,
        pagination: payload?.pagination ?? { page: 1, limit: 20, total: 0 },
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

/** Super admin only: fetch all notifications */
export const fetchAllNotifications = createAsyncThunk(
  "notifications/fetchAllNotifications",
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationService.getAllNotifications(params);
      const payload = response.data?.data ?? response.data;
      return {
        notifications: payload?.notifications ?? [],
        unreadCount: payload?.unreadCount ?? 0,
        pagination: payload?.pagination ?? { page: 1, limit: 20, total: 0 },
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all notifications"
      );
    }
  }
);

export const createNotification = createAsyncThunk(
  "notifications/createNotification",
  async (notificationData, { rejectWithValue }) => {
    try {
      const response = await notificationService.createNotification(
        notificationData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create notification"
      );
    }
  }
);

export const createGlobalNotification = createAsyncThunk(
  "notifications/createGlobalNotification",
  async (notificationData, { rejectWithValue }) => {
    try {
      const response = await notificationService.createGlobalNotification(
        notificationData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create global notification"
      );
    }
  }
);

export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await notificationService.markAsRead(notificationId);
      const payload = response.data?.data ?? response.data;
      return payload?.id ?? notificationId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark notification as read"
      );
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      await notificationService.markAllAsRead();
      const role = getState()?.auth?.user?.role?.slug || getState()?.auth?.user?.role;
      const isSuperAdmin = String(role || "").toLowerCase() === "superadmin";
      if (isSuperAdmin) {
        await dispatch(fetchAllNotifications({}));
      } else {
        await dispatch(fetchNotifications({}));
      }
      return null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark all as read"
      );
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await notificationService.deleteNotification(notificationId);
      const payload = response.data?.data ?? response.data;
      return payload?.id ?? notificationId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete notification"
      );
    }
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
      state.unreadCount += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAllNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Notification
      .addCase(createNotification.fulfilled, (state, action) => {
        const item = action.payload?.data ?? action.payload;
        if (item && typeof item === "object") state.notifications.unshift(item);
      })
      // Mark As Read
      .addCase(markAsRead.fulfilled, (state, action) => {
        const id = typeof action.payload === "object" ? action.payload?.id : action.payload;
        const notification = state.notifications.find(
          (n) => n.id === id
        );
        if (notification && !notification.isRead) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      // Mark All As Read
      .addCase(markAllAsRead.pending, (state) => {
        state.markAllAsReadLoading = true;
        state.error = null;
      })
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.markAllAsReadLoading = false;
        state.notifications.forEach((n) => {
          n.isRead = true;
        });
        state.unreadCount = 0;
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.markAllAsReadLoading = false;
        state.error = action.payload;
      })
      // Delete Notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const id = typeof action.payload === "object" ? action.payload?.id : action.payload;
        const notification = state.notifications.find(
          (n) => n.id === id
        );
        if (notification && !notification.isRead) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications = state.notifications.filter(
          (n) => n.id !== id
        );
      });
  },
});

export const { clearError, addNotification } = notificationSlice.actions;
export default notificationSlice.reducer;
